/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Workflows Catalog — admin page.
 *
 * Browse the 6 (and counting) production workflows. Each card jumps to
 * the Workflow Detail page where inputs are filled in and a run is
 * started. New customer use cases are new YAML files in V2, not new
 * code (ADR-018 §19).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import workflowAdminApi, { extractWorkflows } from '../../services/workflowAdminApi';
import WorkflowCard from './components/WorkflowCard';
import DemoModeToggle from '../../components/workflows/DemoModeToggle';
import WorkflowDemoChrome from '../../components/workflows/WorkflowDemoChrome';
import './WorkflowsCatalog.css';

const DOMAIN_FILTERS = [
  { value: '', label: 'All' },
  { value: 'wms', label: 'WMS' },
  { value: 'iseries', label: 'iSeries' },
  { value: 'civil', label: 'Civil' },
  { value: 'cross-domain', label: 'Cross-Domain' },
];

function classifyError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 503) return { kind: 'unavailable', message: 'Workflow Factory is temporarily unavailable.' };
  if (status === 403) return { kind: 'permission', message: body?.error || 'You do not have permission to view workflows.' };
  if (status === 401) return { kind: 'auth', message: 'Session expired. Please sign in again.' };
  return { kind: 'server', message: body?.error || err?.message || 'Failed to load workflows.' };
}

export default function WorkflowsCatalog() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await workflowAdminApi.listWorkflows({ domain: domain || undefined });
      setWorkflows(extractWorkflows(body));
    } catch (err) {
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workflows;
    return workflows.filter((w) => {
      const hay = [w.name, w.display_name, w.id, w.workflow_id, w.description, w.summary]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [workflows, search]);

  return (
    <AdminLayout>
      <div className="wf-catalog-page">
        <header className="wf-catalog-header">
          <div>
            <h1 className="wf-catalog-title">Workflow Catalog</h1>
            <p className="wf-catalog-subtitle">
              Production-ready workflows you can run end-to-end. New customer
              use cases are new YAML files in V2, not new code.
            </p>
          </div>
          <DemoModeToggle />
        </header>

        <div className="wf-catalog-controls">
          <input
            type="search"
            className="wf-catalog-search"
            placeholder="Search by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="wf-catalog-domains" role="tablist">
            {DOMAIN_FILTERS.map((f) => (
              <button
                key={f.value || 'all'}
                type="button"
                role="tab"
                aria-selected={domain === f.value}
                className={`wf-catalog-pill ${domain === f.value ? 'is-active' : ''}`}
                onClick={() => setDomain(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="wf-catalog-grid" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="wf-card wf-card-skeleton" />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className={`wf-catalog-state wf-catalog-state-${error.kind}`}>
            <h2>Something went wrong</h2>
            <p>{error.message}</p>
            <button type="button" className="wf-btn wf-btn-secondary" onClick={load}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="wf-catalog-state wf-catalog-state-empty">
            <h2>No workflows match</h2>
            <p>
              {workflows.length === 0
                ? 'The catalog is empty. Check that V2 Workflow Factory is reachable.'
                : 'Try a different search term or domain filter.'}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="wf-catalog-grid">
            {filtered.map((w) => (
              <WorkflowCard
                key={w.id || w.workflow_id || w.name}
                workflow={w}
              />
            ))}
          </div>
        )}

        <WorkflowDemoChrome />
      </div>
    </AdminLayout>
  );
}
